<template>
  <div class="flex gap-3" :data-cy="`cf-question-${question.id}`">
    <span
      class="mt-1.5 flex shrink-0 justify-center"
      :data-cy="`cf-state-${question.id}`"
      :data-state="state"
    >
      <span :class="['h-2.5 w-2.5 rounded-full', DOTS[state]]" />
    </span>

    <div class="min-w-0 flex-1">
      <div class="flex flex-wrap items-baseline gap-x-2">
        <span class="text-sm font-medium text-gray-500">
          {{ question.title || "Untitled question" }}
          <span
            v-if="question.required && !unmet"
            class="text-gray-400"
            title="Required"
          >
            *
          </span>
        </span>

        <span
          v-if="!answered && !editing && !unmet"
          class="text-sm text-gray-500"
          :data-cy="`cf-unanswered-${question.id}`"
          title="Not answered"
        >
          &mdash;
        </span>

        <CustomFormBadge
          v-if="unmet"
          tone="warn"
          :data-cy="`cf-unmet-${question.id}`"
        >
          {{ unmetLabel }}
        </CustomFormBadge>

        <CustomFormBadge
          v-if="!question.customer_visible"
          title="Internal only — never shown to the customer"
        >
          Internal
        </CustomFormBadge>
      </div>

      <p v-if="question.description" class="mt-1 text-xs text-gray-500">
        {{ question.description }}
      </p>

      <!-- The answer is a slot so TC-351 can drop editable inputs in without
           reimplementing the surrounding label, notes, photo, and author
           chrome. -->
      <div v-if="editing || hasReadOnlyAnswer" class="mt-1">
        <slot name="answer" :question="question" :answer="answer">
          <div
            v-if="signature"
            :data-cy="`cf-signature-${question.id}`"
            class="text-sm"
          >
            <img
              :src="signature.image_url"
              alt="Captured signature"
              class="h-16 border border-gray-200 bg-white"
            />
            <span v-if="signature.signed_at" class="text-xs text-gray-500">
              Signed {{ formatDateTime(signature.signed_at) }}
            </span>
          </div>
          <CustomFormStatusValue
            v-else-if="statusToken"
            :token="statusToken"
            :label="displayValue"
            :data-cy="`cf-answer-${question.id}`"
          />
          <p
            v-else-if="displayValue"
            class="text-base font-semibold text-gray-900"
            :data-cy="`cf-answer-${question.id}`"
          >
            {{ displayValue }}
          </p>
        </slot>
      </div>

      <p
        v-if="answer && answer.note"
        class="mt-1 text-xs text-gray-500"
        :data-cy="`cf-note-${question.id}`"
      >
        {{ answer.note }}
      </p>

      <div
        v-if="photos.length"
        class="mt-2 flex flex-wrap gap-2"
        :data-cy="`cf-photos-${question.id}`"
      >
        <button
          v-for="(photo, index) in photos"
          :key="photo.id"
          type="button"
          class="h-20 w-20 overflow-hidden rounded border border-gray-200 hover:border-gray-400"
          :title="photo.caption || undefined"
          :data-cy="`cf-photo-${photo.id}`"
          @click="openIndex = index"
        >
          <img
            :src="photo.file.thumb.url"
            :alt="photo.caption || 'Answer photo'"
            class="h-full w-full object-cover"
          />
        </button>
      </div>

      <p
        v-if="showAuthor && answer && answer.created_by_name"
        class="mt-1 text-xs text-gray-500"
        :data-cy="`cf-author-${question.id}`"
      >
        Answered by {{ answer.created_by_name }}
      </p>
    </div>

    <!-- Read-only: answer photos have no caption endpoint behind them here. -->
    <PhotoViewerModal
      v-if="viewerPhoto"
      :photo="viewerPhoto"
      readonly
      :can-go-previous="openIndex > 0"
      :can-go-next="openIndex < photos.length - 1"
      :go-previous="() => step(-1)"
      :go-next="() => step(1)"
      @hide-modal="openIndex = -1"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from "vue";

import PhotoViewerModal from "@/PhotoGallery/PhotoViewerModal.vue";
import type {
  CustomFormAnswer,
  CustomFormSubmissionQuestion
} from "@/typings/customForm";
import { answerPhotoToPhoto } from "./answerPhotoToPhoto";
import {
  answerDisplayValue,
  answerFieldType,
  formatDateTime
} from "./customFormAnswerDisplay";
import CustomFormBadge from "./CustomFormBadge.vue";
import { questionState } from "./customFormQuestionState";
import CustomFormStatusValue from "./CustomFormStatusValue.vue";

const props = withDefaults(
  defineProps<{
    question: CustomFormSubmissionQuestion;
    answer: CustomFormAnswer | null;
    showAuthor?: boolean;
    // Whether an input is being supplied through the #answer slot. A prop rather
    // than useSlots(), because slot presence is not a reactive dependency — a
    // computed over it never re-evaluates when the parent starts supplying one.
    editing?: boolean;
  }>(),
  { showAuthor: true, editing: false }
);

const DOTS: Record<string, string> = {
  satisfied: "bg-green-500",
  unmet: "bg-red-500",
  optional: "border border-gray-300 bg-white"
};

const fieldType = computed(() => answerFieldType(props.question, props.answer));

const isSignature = computed(() => fieldType.value === "signature");
const isPhotoAnswer = computed(() => fieldType.value === "photo_only");

const photos = computed(() => props.answer?.photos ?? []);

// Only set once actually signed; the admin portal never draws a signature.
const signature = computed(() =>
  isSignature.value ? (props.answer?.signature ?? null) : null
);

const answered = computed(() => props.answer?.answered ?? false);

const state = computed(() => questionState(props.question, props.answer));

const unmet = computed(() => state.value === "unmet");

// An unmet photo-only question is always short on photos; any other type is
// short on photos only once it already has a value.
//
// The count guard is not theoretical: the builder coerces a minimum below 1 up to
// 1, but the server permits 0 (numericality allows it and publish_blockers does
// not check it), so an API-authored template can reach here and ask for
// "Needs 0 photos".
const unmetLabel = computed(() => {
  const count = props.question.photo_min_count;
  if (count < 1) return "Required";
  if (!isPhotoAnswer.value && !answered.value) return "Required";

  return `Needs ${count} photo${count === 1 ? "" : "s"}`;
});

const displayValue = computed(() =>
  answerDisplayValue(props.question, props.answer)
);

const statusToken = computed(() =>
  fieldType.value === "status_ryg"
    ? (props.answer?.selected_tokens?.[0] ?? null)
    : null
);

const hasReadOnlyAnswer = computed(
  () => !!signature.value || !!statusToken.value || !!displayValue.value
);

const openIndex = ref(-1);

const viewerPhoto = computed(() =>
  openIndex.value < 0
    ? null
    : answerPhotoToPhoto(photos.value[openIndex.value], props.question.title)
);

function step(delta: number) {
  openIndex.value += delta;
}
</script>

<style scoped></style>
